import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

const Bullet = styled.div`
  position: relative;
  flex-shrink: 0;
  box-sizing: border-box;
  margin-right: 11px;
  width: 24px;
  height: 24px;
  border-radius: 50%;

  &:before {
    position: absolute;
    top: 6px;
    left: 6px;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    content: "";
    transform: scale(0);
    transition: transform 0.1s;
  }

  ${({
    theme,
    checked, disabled
  }) => css`
    border: 1px solid ${theme.colors.neutral.grey[4]};

    ${checked && css`
      border-color: ${theme.colors.primary.purple} !important; 
      background-color: ${theme.colors.primary.purple} !important;

      &:before {
        background: ${theme.colors.neutral.white} !important;
        transform: scale(1);
      }
    `};

    ${disabled && css`
      border-color: ${theme.colors.neutral.grey[3]} !important;
      background: ${theme.colors.neutral.grey[1]} !important;

      &:before {
        background: ${theme.colors.neutral.grey[1]} !important;
      }
    `};
  `};
`;

const Label = styled.label`
  display: flex;
  align-items: center;
  margin: 5px 0;
  padding: 0;
  width: 100%;
  cursor: pointer;

  &:hover {
    ${Bullet} {
      background-color: ${({ theme }) => theme.colors.semantic.lightPurple};
      box-shadow: 0 0 6px 0 rgba(33, 48, 56, 0.2);

      &:before {
        background: ${({ theme }) => theme.colors.primary.lightPurple};
      }
    }
  };

`;

const Input = styled.input.attrs({ type: 'radio', readOnly: true })`
  opacity: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
`;

const Container = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;

  ${({
    theme,
    button, checked
  }) => css`

    ${button && css`
      display: inline-block;
      overflow: visible;
      padding: 8px 17.5px;
      max-width: 320px;
      width: 100%;
      height: auto;
      outline: 0;
      border: 1px solid ${theme.colors.neutral.grey[4]};
      border-radius: 4px;
      background-color: ${theme.colors.neutral.white};
      vertical-align: middle;
      text-align: left;
      line-height: 1;
      cursor: pointer;
      user-select: none;
    `};
  `};
`;

/**
 * @tags styled, radio, toggle, form field
 */
const Radio = props => (
  <Container
    onClick={props.onChange}
    button={props.button}
    checked={props.checked}
    {...props.attrs.container}
    data-qa={props.qa}
    className={props.className}
  >
    <Label htmlFor={props.id}>
      <Bullet checked={props.checked} disabled={props.disabled} {...props.attrs.bullet}>
        <Input id={props.id} checked={props.checked} {...props.attrs.input} />
      </Bullet>
      {props.children}
    </Label>
  </Container>
);

Radio.displayName = 'Radio';

Radio.propTypes = {
  attrs: PropTypes.shape({
    container: PropTypes.shape({}),
    bullet: PropTypes.shape({}),
    input: PropTypes.shape({})
  }),
  button: PropTypes.bool,
  children: PropTypes.node,
  checked: PropTypes.bool,
  disabled: PropTypes.bool,
  id: PropTypes.string,
  onChange: PropTypes.func,
  qa: PropTypes.string,
  className: PropTypes.string
};

Radio.defaultProps = {
  attrs: {
    container: {},
    bullet: {},
    input: {}
  },
  button: false,
  children: undefined,
  checked: false,
  disabled: false,
  id: undefined,
  onChange: () => {},
  qa: undefined,
  className: undefined
};

/** @component */
export default Radio;
